/* eslint-disable camelcase */
import { QueryFunctionContext } from '@tanstack/react-query';
import { api } from '../services/api';
import { IData, IDataNoPagination } from '../types/shared';
import {
  INfseCertificado,
  INfseDpsForm,
  INfseEmissao,
  INfseEmissoesFiltros,
  INfseEmitente,
  INfseEmpresaBusca,
  INfseIbsCbsServico,
  INfseMunicipio,
  INfsePais,
  INfseServicoSugestao,
  INfsePainel,
  INfsePreviaXml,
  INfseProcuracao,
  INfseProcuradorDaRede,
  INfseTomador,
  INfseTotalizadores,
  INfseUltimaNotaResposta,
  INfseValidacao,
  NfsePeriodo,
} from '../types/nfseEmissao';

const BASE = '/api/v1/nfse-emissao';

export const useNfseEmissao = () => {
  const getPainel = async (key: QueryFunctionContext): Promise<INfsePainel> => {
    const [, periodo] = key.queryKey as [string, NfsePeriodo?];
    const res = await api.get<IDataNoPagination<INfsePainel>>(`${BASE}/painel`, {
      params: { periodo: periodo ?? 'mes' },
    });
    return res.data.data;
  };

  const getEmitentes = async (key: QueryFunctionContext): Promise<INfseEmitente[]> => {
    const [, params] = key.queryKey as [string, { search?: string; situacao?: string }?];
    const res = await api.get<IDataNoPagination<INfseEmitente[]>>(`${BASE}/emitentes`, {
      params,
    });
    return res.data.data;
  };

  const getUltimaNota = async (key: QueryFunctionContext): Promise<INfseUltimaNotaResposta> => {
    const [, emitenteId] = key.queryKey as [string, number];
    const res = await api.get<IDataNoPagination<INfseUltimaNotaResposta>>(
      `${BASE}/emitentes/${emitenteId}/ultima-nota`,
    );
    return res.data.data;
  };

  // Série e próxima DPS informadas no card da tela Emitir (sem reenviar o certificado).
  const alterarNumeracao = async (
    emitenteId: number,
    payload: { serie?: string; proximo_n_dps?: number },
  ): Promise<INfseUltimaNotaResposta> => {
    const res = await api.put<IDataNoPagination<INfseUltimaNotaResposta>>(
      `${BASE}/emitentes/${emitenteId}/numeracao`,
      payload,
    );
    return res.data.data;
  };

  const getPadroes = async (key: QueryFunctionContext): Promise<Partial<INfseDpsForm>> => {
    const [, emitenteId] = key.queryKey as [string, number];
    const res = await api.get<IDataNoPagination<Partial<INfseDpsForm>>>(
      `${BASE}/emitentes/${emitenteId}/padroes`,
    );
    return res.data.data;
  };

  // Nome (sem acento e sem caixa) ou código IBGE de 7 dígitos.
  const buscarMunicipios = async (q: string, uf?: string): Promise<INfseMunicipio[]> => {
    const res = await api.get<IDataNoPagination<INfseMunicipio[]>>(`${BASE}/municipios`, {
      params: { q, uf: uf || undefined },
    });
    return res.data.data;
  };

  const getPaises = async (): Promise<INfsePais[]> => {
    const res = await api.get<IDataNoPagination<INfsePais[]>>(`${BASE}/paises`);
    return res.data.data;
  };

  // NBS, cIndOp e cClassTrib que o Anexo VIII aceita para o serviço.
  const getIbsCbs = async (cTribNac: string): Promise<INfseIbsCbsServico> => {
    const res = await api.get<IDataNoPagination<INfseIbsCbsServico>>(`${BASE}/ibscbs/${cTribNac}`);
    return res.data.data;
  };

  // cTribNac pelo código ou pela descrição; sem q, o histórico da empresa.
  const buscarServicos = async (
    emitenteId: number,
    q?: string,
    tomador?: string,
  ): Promise<INfseServicoSugestao[]> => {
    const res = await api.get<IDataNoPagination<INfseServicoSugestao[]>>(
      `${BASE}/emitentes/${emitenteId}/servicos`,
      { params: { q: q || undefined, tomador: tomador || undefined } },
    );
    return res.data.data;
  };

  const getTomador = async (documento: string): Promise<INfseTomador> => {
    const res = await api.get<IDataNoPagination<INfseTomador>>(`${BASE}/tomadores/${documento}`);
    return res.data.data;
  };

  const getEmissoes = async (key: QueryFunctionContext): Promise<IData<INfseEmissao[]>> => {
    const [, params] = key.queryKey as [string, INfseEmissoesFiltros];
    const res = await api.get<IData<INfseEmissao[]>>(`${BASE}/emissoes`, { params });
    return res.data;
  };

  const getTotalizadores = async (key: QueryFunctionContext): Promise<INfseTotalizadores> => {
    const [, params] = key.queryKey as [string, { emitente_id?: number; tomador?: string }];
    const res = await api.get<IDataNoPagination<INfseTotalizadores>>(
      `${BASE}/emissoes/totalizadores`,
      { params },
    );
    return res.data.data;
  };

  const getContagem = async (key: QueryFunctionContext): Promise<Record<string, number>> => {
    const [, params] = key.queryKey as [string, { emitente_id?: number; tomador?: string }];
    const res = await api.get<IDataNoPagination<Record<string, number>>>(
      `${BASE}/emissoes/contagem`,
      { params },
    );
    return res.data.data;
  };

  const previaXml = async (payload: INfseDpsForm): Promise<INfsePreviaXml> => {
    const res = await api.post<IDataNoPagination<INfsePreviaXml>>(
      `${BASE}/emissoes/previa-xml`,
      payload,
    );
    return res.data.data;
  };

  const getEmissao = async (key: QueryFunctionContext): Promise<INfseEmissao> => {
    const [, id] = key.queryKey as [string, number];
    const res = await api.get<IDataNoPagination<INfseEmissao>>(`${BASE}/emissoes/${id}`);
    return res.data.data;
  };

  const createEmissao = async (payload: INfseDpsForm): Promise<INfseEmissao> => {
    const res = await api.post<IDataNoPagination<INfseEmissao>>(`${BASE}/emissoes`, payload);
    return res.data.data;
  };

  const updateEmissao = async (id: number, payload: INfseDpsForm): Promise<INfseEmissao> => {
    const res = await api.put<IDataNoPagination<INfseEmissao>>(`${BASE}/emissoes/${id}`, payload);
    return res.data.data;
  };

  const validarEmissao = async (id: number): Promise<INfseValidacao> => {
    const res = await api.post<IDataNoPagination<INfseValidacao>>(`${BASE}/emissoes/${id}/validar`);
    return res.data.data;
  };

  const enviarEmissao = async (id: number): Promise<INfseEmissao> => {
    const res = await api.post<IDataNoPagination<INfseEmissao>>(`${BASE}/emissoes/${id}/enviar`);
    return res.data.data;
  };

  const downloadArquivo = async (id: number, tipo: 'pdf' | 'xml'): Promise<Blob> => {
    const res = await api.get(`${BASE}/emissoes/${id}/${tipo}`, { responseType: 'blob' });
    return res.data;
  };

  const enviarEmail = async (id: number, para: string, copia?: string): Promise<void> => {
    await api.post(`${BASE}/emissoes/${id}/email`, { para, copia: copia || null });
  };

  const cancelarEmissao = async (
    id: number,
    payload: { motivo: string; justificativa: string },
  ): Promise<INfseEmissao> => {
    const res = await api.post<IDataNoPagination<INfseEmissao>>(
      `${BASE}/emissoes/${id}/cancelar`,
      payload,
    );
    return res.data.data;
  };

  const substituirEmissao = async (id: number): Promise<INfseEmissao> => {
    const res = await api.post<IDataNoPagination<INfseEmissao>>(
      `${BASE}/emissoes/${id}/substituir`,
    );
    return res.data.data;
  };

  const buscarEmpresas = async (q: string): Promise<INfseEmpresaBusca[]> => {
    const res = await api.get<IDataNoPagination<INfseEmpresaBusca[]>>(`${BASE}/empresas`, {
      params: { q },
    });
    return res.data.data;
  };

  const getCertificados = async (): Promise<INfseCertificado[]> => {
    const res = await api.get<IDataNoPagination<INfseCertificado[]>>(`${BASE}/certificados`);
    return res.data.data;
  };

  const enviarCertificado = async (payload: {
    empresa_id: number;
    arquivo: File;
    senha: string;
  }): Promise<INfseCertificado> => {
    const form = new FormData();
    form.append('empresa_id', String(payload.empresa_id));
    form.append('arquivo', payload.arquivo);
    form.append('senha', payload.senha);
    const res = await api.post<IDataNoPagination<INfseCertificado>>(`${BASE}/certificados`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res.data.data;
  };

  // Procuração eletrônica: a empresa emite com o certificado do escritório.
  // CNPJs autorizados como procurador (lista fechada no backend); o primeiro é o padrão.
  const getProcuradores = async (): Promise<INfseProcuradorDaRede[]> => {
    const res = await api.get<IDataNoPagination<INfseProcuradorDaRede[]>>(`${BASE}/procuradores`);
    return res.data.data;
  };

  const definirProcuracao = async (payload: {
    empresa_id: number;
    // Um dos procuradores autorizados; nulo = o único autorizado.
    procurador_empresa_id?: number | null;
    valida_ate?: string | null;
  }): Promise<INfseProcuracao> => {
    const res = await api.put<IDataNoPagination<INfseProcuracao>>(`${BASE}/procuracoes`, {
      ...payload,
      valida_ate: payload.valida_ate || null,
    });
    return res.data.data;
  };

  // Só o Administrador (grupo 1); o backend devolve 403 para os demais.
  const autorizarProcurador = async (
    empresaId: number,
    autorizado: boolean,
  ): Promise<INfseProcuradorDaRede> => {
    const res = await api.put<IDataNoPagination<INfseProcuradorDaRede>>(
      `${BASE}/procuradores/${empresaId}`,
      { autorizado },
    );
    return res.data.data;
  };

  const removerProcuracao = async (empresaId: number): Promise<INfseProcuracao> => {
    const res = await api.delete<IDataNoPagination<INfseProcuracao>>(
      `${BASE}/procuracoes/${empresaId}`,
    );
    return res.data.data;
  };

  return {
    getPainel,
    getEmitentes,
    getUltimaNota,
    alterarNumeracao,
    getPadroes,
    buscarMunicipios,
    getPaises,
    getIbsCbs,
    buscarServicos,
    getTomador,
    getEmissoes,
    getContagem,
    getTotalizadores,
    previaXml,
    getEmissao,
    createEmissao,
    updateEmissao,
    validarEmissao,
    enviarEmissao,
    downloadArquivo,
    enviarEmail,
    cancelarEmissao,
    substituirEmissao,
    buscarEmpresas,
    getCertificados,
    enviarCertificado,
    getProcuradores,
    autorizarProcurador,
    definirProcuracao,
    removerProcuracao,
  };
};
